import { useApi } from "~/composables/useApi";
import { useDocuSeal } from "~/composables/useDocuSeal";
import { useProtectedRoute } from "~/composables/useProtectedRoute";
import { useSupabase } from "~/composables/useSupabase";
import type { Color } from "@asap/design/styles/colors";
import type {
  CommercialConditionFile,
  CommercialConditionRequest,
  CreateCommercialConditionRequest,
} from "@asap/shared/schemas/commercial-condition.schema";
import type {
  CommercialConditionFilesResponse,
  CommercialConditionOption,
  CommercialConditionPaymentTermsOption,
  CommercialConditionType,
} from "@asap/shared/types/commercial-condition";
import type { Database } from "@asap/shared/types/supabase.gen";
import type { CommercialConditionPricingModel, CommercialConditionStatus } from "@asap/shared/utils";
import { getFullName } from "@asap/shared/utils/people";
import { commercialConditionColumns } from "@asap/shared/utils/supabase.columns";
import type { CommercialCondition, TeamMember } from "@asap/shared/utils/supabase.types";
import type { SortingState } from "@tanstack/vue-table";
import { format } from "date-fns";
import dedent from "dedent";
import { schemaCommercialConditionCreate } from "~/zod/schema/commercialCondition.schema";

type CommercialConditionFilters = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  companyId?: string;
  status?: CommercialConditionStatus;
  businessUnitIds?: string[];
  orders: { column: string; direction: "asc" | "desc" }[];
};

type CommercialConditionState = {
  selectedFilters: CommercialConditionFilters;
  loading: boolean;
  totalItems: number;
  showCreateModal: boolean;
  sorting: SortingState;
};

const commercialConditionState = reactive<CommercialConditionState>({
  selectedFilters: {
    pageSize: 20,
    pageIndex: 0,
    orders: [],
    search: undefined,
    companyId: undefined,
    businessUnitIds: undefined,
    status: undefined,
  },
  totalItems: 0,
  showCreateModal: false,
  loading: false,
  sorting: [{ id: "created_at", desc: true }],
});

export const useCommercialCondition = () => {
  const supabase = useSupabase();
  const { invoke } = useApi();
  const { archiveSubmission } = useDocuSeal();

  const convertSortingToOrders = (sorting: SortingState): { column: string; direction: "asc" | "desc" }[] => {
    return sorting.map((sort) => ({
      column: sort.id,
      direction: sort.desc ? "desc" : "asc",
    }));
  };

  const mapButtonStatusToAwVariant = (status: CommercialConditionStatus): Color => {
    switch (status) {
      case "not_signed":
        return "warning";
      case "to_validate":
        return "warning";
      case "signed":
        return "success";
      case "refused":
        return "gray";
      case "canceled":
        return "gray";
      case "sent":
        return "info";
      default:
        return "gray";
    }
  };

  const PRICING_CAPTION_DETAILS: Record<CommercialConditionType, { standard: string; limit: string }> = {
    interim: {
      standard: "coefficient 2,40 et 6 mois pré embauche",
      limit: "un coefficient de 2,20 et 4 mois de pré-embauche",
    },
    recruitment_office: {
      standard: "25% de frais de recrutement",
      limit: "20% de frais de recrutement",
    },
    interim_and_recruitment_office: {
      standard:
        "coefficient 2,40 et 6 mois avant embauche sans frais pour l'intérim et 25% de frais pour le recrutement",
      limit: "un coefficient de 2,20 et 4 mois de pré-embauche pour l'intérim et 20% de frais pour le recrutement",
    },
  };

  const buildCaption = (details: { standard: string; limit: string }) => dedent`
    Les valeurs par défaut indiquent les conditions commerciales standardes de asap.work (${details.standard}).

    Vous avez la possibilité de descendre jusqu'à ${details.limit}. En dessous de ces valeurs, une validation de votre manager sera obligatoire.
  `;

  const PRICING_CAPTIONS: Record<CommercialConditionType, string> = {
    interim: buildCaption(PRICING_CAPTION_DETAILS.interim),
    recruitment_office: buildCaption(PRICING_CAPTION_DETAILS.recruitment_office),
    interim_and_recruitment_office: buildCaption(PRICING_CAPTION_DETAILS.interim_and_recruitment_office),
  };

  const DEFAULT_GUARANTEES = dedent`
  - Minimum 2 références réalisées chez les anciens employeurs sur le plan humain et sur le plan technique (sous réserve d'avoir à minima trois expériences professionnelles)
  - Une vérification des diplômes et d'une autorisation à travailler sur le territoire français
  - Envoi des profils avec un compte rendu établi au préalable
`;

  const DEFAULT_RECRUITMENT_OFFICE_GUARANTEES = dedent`
  ${DEFAULT_GUARANTEES}
  - Clause de remplacement : nous nous engageons à proposer des profils identiques si le candidat venait à quitter votre entreprise pendant la période d'essai
`;

  const COMMERCIAL_CONDITIONS_TEMPLATES: CommercialConditionOption[] = [
    {
      type: "interim",
      pricingModel: "profile",
      label: "En fonction du profil",
      pricing: dedent`
      - Ouvrier : coeff. 2.40 - mission de 6 mois avant embauche sans frais
      - Etam : coeff. 2.40 - mission de 6 mois avant embauche sans frais
      - Cadre : coeff. 2.40 - mission de 6 mois avant embauche sans frais
    `,
    },
    {
      type: "interim",
      pricingModel: "grid",
      label: "En fonction d'une grille de rémunération",
      pricing: dedent`
      - SMIC - X€ annuel brut : coeff. 2.40 - mission de 6 mois avant embauche sans frais
      - X€ - Y€ annuel brut : coeff. 2.40 - mission de 6 mois avant embauche sans frais
      - plus de Y€ annuel brut : coeff. 2.40 - mission de 6 mois avant embauche sans frais
    `,
    },
    {
      type: "interim",
      pricingModel: "unified",
      label: "Tarif unique",
      pricing: dedent`
        Coeff. 2.40 - mission de 6 mois avant embauche sans frais
      `,
    },
    {
      type: "recruitment_office",
      pricingModel: "profile",
      label: "En fonction du profil",
      pricing: dedent`
      - Profil Ouvrier : 25% du salaire annuel brut
      - Profil Etam : 25% du salaire annuel brut
      - Profil Cadre : 25% du salaire annuel brut
    `,
    },
    {
      type: "recruitment_office",
      pricingModel: "grid",
      label: "En fonction d'une grille de rémunération",
      pricing: dedent`
      - SMIC - X€ annuel brut : 25% du salaire annuel brut
      - X€ - Y€ annuel brut : 25% du salaire annuel brut
      - plus de Y€ annuel brut : 25% du salaire annuel brut
    `,
    },
    {
      type: "recruitment_office",
      pricingModel: "unified",
      label: "Tarif unique",
      pricing: dedent`
        25% du salaire annuel brut
      `,
    },
    {
      type: "interim_and_recruitment_office",
      pricingModel: "profile",
      label: "En fonction du profil",
      pricing: dedent`
      - Ouvrier : coeff. 2.40 - mission de 6 mois avant embauche sans frais (intérim) / 25% du salaire annuel brut (recrutement)
      - Etam : coeff. 2.40 - mission de 6 mois avant embauche sans frais (intérim) / 25% du salaire annuel brut (recrutement)
      - Cadre : coeff. 2.40 - mission de 6 mois avant embauche sans frais (intérim) / 25% du salaire annuel brut (recrutement)
    `,
    },
    {
      type: "interim_and_recruitment_office",
      pricingModel: "grid",
      label: "En fonction d'une grille de rémunération",
      pricing: dedent`
      - SMIC - X€ annuel brut : coeff. 2.40 - mission de 6 mois avant embauche sans frais (intérim) / 25% du salaire annuel brut (recrutement)
      - X€ - Y€ annuel brut : coeff. 2.40 - mission de 6 mois avant embauche sans frais (intérim) / 25% du salaire annuel brut (recrutement)
      - plus de Y€ annuel brut : coeff. 2.40 - mission de 6 mois avant embauche sans frais (intérim) / 25% du salaire annuel brut (recrutement)
    `,
    },
    {
      type: "interim_and_recruitment_office",
      pricingModel: "unified",
      label: "Tarif unique",
      pricing: dedent`
        Intérim : coeff. 2.40 - mission de 6 mois avant embauche sans frais \nRecrutement : 25% du salaire annuel brut
      `,
    },
  ];

  const PAYMENT_TERMS_OPTIONS: CommercialConditionPaymentTermsOption[] = [
    {
      type: "interim",
      model: "interim_1",
      label: "Modèle 1",
      terms: "45 jours",
    },
    {
      type: "recruitment_office",
      model: "recruitment_office_1",
      label: "Selon la disponibilité du candidat",
      terms:
        "- Si candidat en poste : 30% des honoraires à l'acceptation du candidat, puis 40% à la prise de poste, puis 30% à la fin de la période d'essai, paiement comptant. \n- Si candidat disponible : 70% des honoraires à la prise de poste, puis 30% à la fin de la période d'essai, paiement comptant.",
    },
    {
      type: "recruitment_office",
      model: "recruitment_office_2",
      label: "100% des honoraires",
      terms: "100% des honoraires lors de la prise de poste, paiement comptant",
    },
    {
      type: "interim_and_recruitment_office",
      model: "interim_and_recruitment_office_1",
      label: "Selon la disponibilité du candidat (recrutement)",
      terms:
        "Intérim : 45 jours, à date de facture \nRecrutement : \n- Si candidat disponible : 70% des honoraires à la prise de poste, puis 30% à la fin de la période d'essai, paiement comptant \n- Si candidat en poste : 30% des honoraires à l'acceptation du candidat, puis 40% à la prise de poste, puis 30% à la fin de la période d'essai, paiement comptant",
    },
    {
      type: "interim_and_recruitment_office",
      model: "interim_and_recruitment_office_2",
      label: "100% des honoraires (recrutement)",
      terms:
        "Intérim : 45 jours, à date de facture \nRecrutement : 100% des honoraires lors de la prise de poste, paiement comptant",
    },
  ];

  const fetchFiles = async (id: string): Promise<CommercialConditionFilesResponse> => {
    const { invoke } = useApi();

    try {
      const response = await invoke({
        path: "commercial-condition/:id/files",
        method: "GET",
        params: { id },
      });

      if (!response.success) {
        const message = response.error.message || `Error Response: ${JSON.stringify(response)}`;
        throw new Error(`Fichiers de conditions commerciales introuvables: ${message}`);
      }

      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Fichiers de conditions commerciales introuvables: ${error.message}`, { cause: error });
      }

      throw error;
    }
  };

  const generateDocument = async (
    commercialCondition: CommercialCondition,
    teamMember: TeamMember
  ): Promise<CommercialConditionFile> => {
    const parsedData = schemaCommercialConditionCreate.parse(commercialCondition);
    if (!commercialCondition.company)
      throw new Error("[useCommercialCondition - generateDocument] Company is required");

    const body = {
      created_by_name: getFullName(teamMember),
      created_by_email: teamMember.email,
      date: format(new Date(), "dd/MM/yy"),
      commercial_condition_type: parsedData.type,
      ...parsedData,
    };

    const { checkTokens, refreshTokens } = useProtectedRoute();
    if (!checkTokens()) await refreshTokens();

    const response = await invoke({
      path: "commercial-condition/:id/generate",
      method: "POST",
      params: { id: commercialCondition.id },
      body,
    });

    if (!response.success)
      throw new Error("[useCommercialCondition - generateDocument] Failed to generate commercial condition");

    return response.data;
  };

  const update = async (id: string, input: Database["public"]["Tables"]["commercial_condition"]["Update"]) => {
    const { data, error } = await supabase
      .from("commercial_condition")
      .update(input)
      .eq("id", id)
      .select(commercialConditionColumns)
      .single();
    if (error) throw new Error(error.message);
    return data;
  };

  const cancelSubmission = async (commercialCondition: CommercialCondition) => {
    if (!commercialCondition.docuseal_submission_id) return commercialCondition;
    await archiveSubmission(commercialCondition.docuseal_submission_id);
    return update(commercialCondition.id, {
      status: "canceled",
      docuseal_submission_url: null,
      docuseal_sent_by: null,
      docuseal_submission_id: null,
      docuseal_is_archived: true,
    });
  };

  const selectSingle = async function (id: string) {
    const { data, error } = await supabase
      .from("commercial_condition")
      .select(commercialConditionColumns)
      .eq("id", id)
      .single();
    if (error) throw new Error(error.message);
    return data;
  };

  // Goes through the API so the creation emits CommercialConditionCreated; the row is read back
  // for the full shape the caller needs.
  // Goes through the API so the validation emits CommercialConditionValidated.
  const validate = async (id: string) => {
    const response = await invoke({ path: "commercial-condition/:id/validate", method: "POST", params: { id } });

    if (!response.success)
      throw new Error("[useCommercialCondition - validate] Failed to validate commercial condition");

    return response.data;
  };

  const insert = async (input: CreateCommercialConditionRequest) => {
    const response = await invoke({ path: "commercial-condition", method: "POST", body: input });

    if (!response.success) throw new Error("[useCommercialCondition - insert] Failed to create commercial condition");

    return selectSingle(response.data.id);
  };

  const loadCommercialConditionTemplate = function (params: {
    type: CommercialConditionType;
    model: CommercialConditionPricingModel;
  }) {
    const commercialConditionTemplate = COMMERCIAL_CONDITIONS_TEMPLATES.find(
      (cc) => cc.type === params.type && cc.pricingModel === params.model
    );

    if (!commercialConditionTemplate) throw new Error("Commercial condition template not found");

    return commercialConditionTemplate;
  };

  const selectMultipleForTable = async function (
    filters: CommercialConditionFilters
  ): Promise<{ data: CommercialConditionRequest[]; count: number }> {
    commercialConditionState.loading = true;
    const order = filters.orders.at(0);

    const response = await invoke({
      path: "commercial-conditions",
      method: "GET",
      query: {
        pageIndex: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        orderColumn: order?.column,
        orderDirection: order?.direction,
        companyId: filters.companyId,
        status: filters.status,
        businessUnitIds: filters.businessUnitIds,
      },
    });

    commercialConditionState.loading = false;

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return { data: response.data.items, count: response.data.totalItems };
  };

  const uploadSignedFile = async (commercialConditionId: string, file: File): Promise<CommercialConditionFile> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await invoke({
      path: "commercial-condition/:id/signed-file",
      method: "POST",
      params: { id: commercialConditionId },
      body: formData,
    });

    if (!response.success) throw new Error("[useCommercialCondition - uploadSignedFile] Failed to upload signed file");

    return response.data;
  };

  return {
    update,
    validate,
    cancelSubmission,
    selectSingle,
    insert,
    fetchFiles,
    generateDocument,
    uploadSignedFile,
    mapButtonStatusToAwVariant,
    commercialConditionState,
    convertSortingToOrders,
    selectMultipleForTable,
    loadCommercialConditionTemplate,
    DEFAULT_GUARANTEES,
    DEFAULT_RECRUITMENT_OFFICE_GUARANTEES,
    PAYMENT_TERMS_OPTIONS,
    COMMERCIAL_CONDITIONS_TEMPLATES,
    PRICING_CAPTIONS,
  };
};
